<template>
  <div class="i-self-improvement">
    <div>
      <iBaseHeader
        highlight-text="Bonus"
        :version="2"
        font-size-highlighted-text="16px"
        text=""
        align="start"
      />

      <div class="intro">
        <iBaseHeader
          highlight-text=""
          text="Tips for Self-improvement"
          align="start"
        />
        <div>
          People with similar personalities to yours have found these books helpful in overcoming challenges and
          building on their strengths.
        </div>
      </div>

      <div class="books">
        <div
          v-for="(capboi, index) in highestCapboiScores"
          :key="index"
          class="book"
        >
          <div
            class="left"
            @click="openUrl(BOOK_DATA_BY_CAPBOI[capboi].link)"
          >
            <img
              class="image"
              :src="BOOK_DATA_BY_CAPBOI[capboi].image"
            />
          </div>
          <div class="right">
            <div class="about">
              <div class="title">{{ BOOK_DATA_BY_CAPBOI[capboi].title }}</div>
              <div>{{ BOOK_DATA_BY_CAPBOI[capboi].description }}</div>
            </div>
            <div class="authors">
              By: <span class="author">{{ BOOK_DATA_BY_CAPBOI[capboi].author }}</span>
            </div>
            <iBaseButton
              class="cta"
              v-bind="BUTTON_STYLE"
              text="Check on Amazon"
              @clicked="openUrl(BOOK_DATA_BY_CAPBOI[capboi].link)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { black, sorbus400 } from "@/styles/colors"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import BookPragmatic from "@/assets/personality/pragmatic-book-rec.png"
import BookAnalytical from "@/assets/personality/analytical-book-rec.png"
import BookBold from "@/assets/personality/bold-book-rec.png"
import BookCommunal from "@/assets/personality/communal-book-rec.png"
import BookImaginative from "@/assets/personality/imaginative-book-rec.png"
import BookOrthodox from "@/assets/personality/orthodox-book-rec.png"

// TYPES
type BookDataByCapboiType = {
  [key: string]: {
    title: string
    description: string
    image: string
    author: string
    link: string
  }
}

// STORES
const reportStore = useReportStore()

// CONSTANTS
const BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "100%",
  backgroundColor: sorbus400,
  borderRadius: "8px",
  textColor: black,
  padding: "12px 12px"
}

const BOOK_DATA_BY_CAPBOI: BookDataByCapboiType = {
  Communal: {
    title: "How to Win Friends & Influence People",
    description: "The Only Book You Need to Lead You to Success",
    image: BookCommunal,
    author: "Dale Carnegie",
    link: "https://www.amazon.com/How-Win-Friends-Influence-People/dp/B09KT5CRL6?crid=1ELEC23AVP70R&dib=eyJ2IjoiMSJ9.7c9Fi1iXXCEpHRcIbSnSFPOX9SblBRJducNR33FvtcaK-GWXqjKzhEhWDtJ4lJstDufPIQMH0oJ1luwRO3pTm8F916oB8jNQR8_Z-VySpTUnVluUKh_HpXCeueFTrLFhJzpXZyPf0rDtbBZb-O6WlsVPYTpxSEDgNd8kAmXP3oOVd9q-jNnopiqYNXTIK0zN1aMqSFqR6MIVEAflKVQLvvhFyOQfODkGYDEylndjwKI.21hNOWQkcp3TLEaXrWJhLQa2hUPWSTXU5lwYG9FOJ5M&dib_tag=se&keywords=How+to+Win+Friends+%26+Influence+People&qid=1726451272&sprefix=how+to+win+friends+%26+influence+people%2Caps%2C125&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=a3a24cbcd27cf961517411615ad812d9&language=en_US&ref_=as_li_ss_tl"
  },
  Analytical: {
    title: "Essentialism",
    description: "The Disciplined Pursuit of Less",
    image: BookAnalytical,
    author: "Greg McKeown",
    link: "https://www.amazon.com/Essentialism-Greg-McKeown-audiobook/dp/B00IWYP5NI?crid=1Z60MKU1WS7P7&dib=eyJ2IjoiMSJ9.84_7lfyDEwoRzGjYiPtJLxGQ63m1Tcf485TerXJznRV4WdQyFWXYzcYsOKWx-Zt9JPBT3L5b6DReJw5LBc7eRniXd1rXTdCULAHQLFKAxbJl_Zyl15Cqd1timRbegY_Lxqzfhwm00GQI0uUNYktfFkG4h0515B5gp-9gmFsBURrdAtqhvxIG-uYKNVfgB-G_Zhc3C_SvjBd3Vd7cZtoSsHw8zAArd_iqvbHuFmOTW94.uMxbR1bdeirl9gu5r_-XwaEeu-EXpCv_7P3evKv7mSc&dib_tag=se&keywords=Essentialism&qid=1726451383&sprefix=essentialism%2Caps%2C141&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=fcd9dd1b4dd28d7b3ac4b36a6474b63d&language=en_US&ref_=as_li_ss_tl"
  },
  Pragmatic: {
    title: "Atomic Habits",
    description: "An Easy & Proven Way to Build Good Habits & Break Bad Ones",
    image: BookPragmatic,
    author: "James Clear",
    link: "https://www.amazon.com/Atomic-Habits-James-Clear-audiobook/dp/B07RFSSYBH?crid=G945XVDKRYFO&dib=eyJ2IjoiMSJ9.desXBN_Qe9kRWhqrsJgXDXNWOySgseDIoKBf26oniPsYySMa2fw0kzY0UhdbyjVhQ0JXF5SIkjUVqcaH_w-ZX7BaLmJSPYyP9HGKXjonMi5hYWIAXlnv0U0t_4FTwP2PkO3csDz1vgBRm0Hmwti4j403ymXFEAMF6-TShc8tJndqXzpCrNs-s1htt5q0fZurTu60Z56v-PDzXxZLv5xwmSj-LWZq-BXJ3v0mrVc_5co.qqGcMsxrBQXH1GQGT8X8JMu2QrJBfC_vzmEYM9GgxO4&dib_tag=se&keywords=Atomic+Habits&qid=1726451402&sprefix=atomic+habits%2Caps%2C138&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=9472eb77d566371b3db8a342b5dcfd85&language=en_US&ref_=as_li_ss_tl"
  },
  Bold: {
    title: "The Compound Effect",
    description: "Jumpstart Your Income, Your Life, Your Success",
    image: BookBold,
    author: "Darren Hardy",
    link: "https://www.amazon.com/The-Compound-Effect-Darren-Hardy-audiobook/dp/B07MWCVQ1T?crid=2IRAH96APA58J&dib=eyJ2IjoiMSJ9.x9Ndr3OY9fTEDHYM9f5SgsGOvvl0hJt_ZBiQFdgm4KiOl3xQ4tmkAhIQ2DxIw1P0TlArRj84a20qYc2k9CX7m6iYuncdevl0Ib7Aet6mOc1lXecpSg0_VtDh2SY7HLX46t1uAxbPhrljyHyYGLrO-zpbrS7rwfq9mPk5lnUUYl4-cnVfH43BPEOzSq_eo0WSIRbK4ndm7fro7geW4OGQ11Zk8ZHCJod1JMfbTHBJOHY.lUhLJSD9Z-WiRi8PE3evxjo_bFs7ay7LFxdZgKG-B5o&dib_tag=se&keywords=The+Compound+Effect&qid=1726451292&sprefix=%2Caps%2C69&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=e2680a45c197a8f13d54b522ecaa528b&language=en_US&ref_=as_li_ss_tl"
  },
  Orthodox: {
    title: "The 7 Habits of Highly Effective People",
    description: "Powerful Lessons in Personal Change",
    image: BookOrthodox,
    author: "Stephen R. Covey",
    link: "https://www.amazon.com/Habits-Highly-Effective-People-Anniversary/dp/B086DD5KSJ?crid=113E3280WB09K&dib=eyJ2IjoiMSJ9.SqwOwJ5zN4dTSawslkpGL3HNdNsaU4DRSacbUhSuOe9QSksJELTblUOXoRreUVoHopHscGHVFcuKKmY0Cl8FA5ouGxEUxY3FnIUfgBXQMfGgeRzarOVqo9UExVfO1Ow_2686BG6xu-qoozDSKpLdbLd9N1C_Tkh2DsoDJL5V-PH1vR6moSOhIF-d6b1wPbUcospEDcpsuR_mj7AoXhsBNk5s37EbA4Xvu59oOrrAkTo.kE2XZstOXIIPOl2WEpIkuFfWukixZ2ud3qj0Tp5Ob-k&dib_tag=se&keywords=The+7+Habits+of+Highly+Effective+People&qid=1726451223&sprefix=the+7+habits+of+highly+effective+people%2Caps%2C99&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=ecff20035e23e62e9b3ac5f1ad94e006&language=en_US&ref_=as_li_ss_tl"
  },
  Imaginative: {
    title: "Start With Why",
    description: "How Great Leaders Inspire Everyone to Take Action",
    image: BookImaginative,
    author: "Simon Sinek",
    link: "https://www.amazon.com/Start-with-Why-Simon-Sinek-audiobook/dp/B074VF6ZLM?crid=3QS2XRM51PPX5&dib=eyJ2IjoiMSJ9.4kIDXmyJjS0Xw-QcilAjNfu116xDEwKfo5PQ3NL2BYGesse0DCeTzg2HlVXKMEhph1e3-ytN7-og-hP_Q4PvwKXLqpS6HHt06ObvcSvFl3O8yOJqETJo1vRIIwcK7A69AvkJHmEyZHk_DzSubLYqu4zkYENOTytJUCdZS5faPk6y6oc4rYeM9vVtMGEHD3idTvr3yIUqNW7xzB-kDur-qhIyzrdHNk00f_Sk99zex9k.rcpB9RF3qnor3Yt4rf0ud3v6x68PpzrqY7BsIPAlqHU&dib_tag=se&keywords=Start+With+Why&qid=1726451251&sprefix=start+with+why%2Caps%2C81&sr=8-1&linkCode=ll1&tag=jobtestorg07-20&linkId=e5a2db45c2b7eef9b4bd66c6f3aa4932&language=en_US&ref_=as_li_ss_tl"
  }
}

// COMPUTED
const highestCapboiScores = computed(() => reportStore.getCapboiTopTwoTypes)

// FUNCTIONS
function openUrl(redirectUrl: string) {
  window.open(redirectUrl, "_blank")
}
</script>

<style scoped lang="scss">
.i-self-improvement {
  color: $pickle-bluewood900;

  .intro {
    @include flex(column, start, stretch, $gap: 12px);

    @media screen and (min-width: $breakpoint-md) {
      max-width: 560px;
    }
  }

  .books {
    @include flex(column, start, stretch, $gap: 35px);

    margin-top: 35px;

    .book {
      @include flex(column, start, stretch, $gap: 20px);

      @media screen and (min-width: $breakpoint-sm) {
        @include flex(row, space-between, stretch, $gap: 20px);
      }

      @media screen and (min-width: $breakpoint-md) {
        gap: 50px;
      }

      .left {
        width: 100%;
        height: 209px;

        @include border-radius-md;

        background-color: $azure-radiance50;
        position: relative;
        overflow: hidden;
        cursor: pointer;

        @media screen and (min-width: $breakpoint-sm) {
          width: 48%;
        }

        &:hover {
          filter: brightness(97%);
        }

        .image {
          background:
            linear-gradient(white, white) padding-box,
            linear-gradient(to bottom, #e7b284, #fff) border-box;
          border: 1px solid transparent;

          @include border-radius-md;

          position: absolute;
          width: 60%;
          margin: auto;
          top: 10%;
          left: 50%;
          transform: translateX(-50%);

          @media screen and (min-width: $breakpoint-sm) {
            width: 80%;
          }

          @media screen and (min-width: $breakpoint-md) {
            width: 60%;
          }
        }
      }

      .right {
        @include flex(column, center, start, $gap: 20px);

        width: 100%;

        @media screen and (min-width: $breakpoint-sm) {
          @include flex(column, end, start, $gap: 25px);

          width: 52%;
        }

        .about {
          @include text-sm;

          .title {
            @include text-md;

            font-weight: 700;
            margin-bottom: 2px;
          }
        }

        .authors {
          @include text-sm;

          color: $black500;

          .author {
            text-decoration: underline;
            color: $pickle-bluewood900;
          }
        }

        .cta {
          margin-top: 12px;
        }
      }
    }
  }
}
</style>
